import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axiosInstance from '../services/axiosInstance';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Grid,
  CircularProgress, Tooltip, Alert, InputAdornment, Select, FormControl, InputLabel,
} from '@mui/material';
import { Add, Edit, Delete, Visibility, Receipt, Undo, Cancel, PostAdd, Search } from '@mui/icons-material';
import cpvApi from '../services/cpvApi';
import accountApi from '../services/accountApi';
import { formatCurrency } from '../utils/currency';
import { apiSuccess, apiError } from '../utils/toast';
import usePermissions from '../hooks/usePermissions';
import SearchableSelect from '../components/Common/SearchableSelect';
import QuickCreate from '../components/QuickCreate/QuickCreate';
import PaginationBar from '../components/PaginationBar';

const CASH_ACCOUNT_TYPES = ['Cash', 'Bank', 'cash', 'bank'];

const emptyLine = () => ({ accountId: '', description: '', amount: '', taxRate: '0', taxAmount: '0' });

const CashPaymentVouchers = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', cashAccountId: '', startDate: '', endDate: '' });
  const [dialog, setDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [form, setForm] = useState({
    voucherDate: new Date().toISOString().split('T')[0],
    cashAccountId: '',
    payeeType: 'other',
    payeeName: '',
    referenceNumber: '',
    description: '',
    paymentMethod: 'cash',
    lines: [emptyLine()],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [defaultCashAccountId, setDefaultCashAccountId] = useState('');
  const [reasonDialog, setReasonDialog] = useState({ open: false, action: null, target: null });
  const [reasonText, setReasonText] = useState('');

  const makeEmptyForm = () => ({
    voucherDate: new Date().toISOString().split('T')[0],
    cashAccountId: defaultCashAccountId || '',
    payeeType: 'other',
    payeeName: '',
    referenceNumber: '',
    description: '',
    paymentMethod: 'cash',
    lines: [emptyLine()],
  });

  const loadVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset: (page - 1) * limit };
      if (search) params.search = search;
      if (filters.status) params.status = filters.status;
      if (filters.cashAccountId) params.cashAccountId = filters.cashAccountId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const res = await cpvApi.list(params);
      setVouchers(res.data || []);
      setTotal(res.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filters, page, limit]);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await accountApi.getAll({ limit: 500 });
      const all = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      // Show the full Chart of Accounts in the Account lines dropdown
      setAccounts(all);
      const cashAccs = all.filter(a => {
        const type = (a.type || a.accountType || '').toLowerCase();
        const name = (a.name || '').toLowerCase();
        return type === 'cash' || type === 'bank' || name.includes('cash') || name.includes('petty');
      });
      setCashAccounts(cashAccs);

      // Load default cash account from system config
      try {
        const cfgRes = await axiosInstance.get('/settings');
        const configMap = cfgRes.data?.data?.configs || cfgRes.data?.configs || cfgRes.data || {};
        const purchaseCfg = configMap.purchase || {};
        const defaultCashId = purchaseCfg.default_cash_account;
        console.log('Default cash account from config:', defaultCashId, 'available cash accounts:', cashAccs.length);
        if (defaultCashId && cashAccs.some(a => a.id === defaultCashId)) {
          setDefaultCashAccountId(defaultCashId);
        }
      } catch { /* ignore */ }
    } catch (err) { console.error('Failed to load accounts:', err); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadVouchers(); }, [loadVouchers]);

  const handleFilterChange = (e) => { const { name, value } = e.target; setFilters(prev => ({ ...prev, [name]: value })); };

  const handleClearFilters = () => {
    setSearch('');
    setFilters({ status: '', cashAccountId: '', startDate: '', endDate: '' });
  };

  const openCreate = () => {
    setSelected(null);
    setForm(makeEmptyForm());
    setError(null);
    setDialog(true);
  };

  const openEdit = async (v) => {
    try {
      const res = await cpvApi.getById(v.id);
      const d = res.data;
      const lines = (d.lines || []).map(l => ({
        accountId: l.accountId || '',
        description: l.description || '',
        amount: l.amount?.toString() || '',
        taxRate: l.taxRate?.toString() || '0',
        taxAmount: l.taxAmount?.toString() || '0',
      }));
      setSelected(d);
      setForm({
        voucherDate: d.voucherDate?.split('T')[0] || '',
        cashAccountId: d.cashAccountId || '',
        payeeType: d.payeeType || 'other',
        payeeName: d.payeeName || '',
        referenceNumber: d.referenceNumber || '',
        description: d.description || '',
        paymentMethod: d.paymentMethod || 'cash',
        lines: lines.length > 0 ? lines : [emptyLine()],
      });
      setError(null);
      setDialog(true);
    } catch (err) { console.error(err); }
  };

  const openView = async (v) => {
    try {
      const res = await cpvApi.getById(v.id);
      setSelected(res.data);
      setViewDialog(true);
    } catch (err) { console.error(err); }
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (idx) => {
    if (form.lines.length <= 1) return;
    setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  };
  const updateLine = (idx, field, value) => {
    setForm(f => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      if (field === 'amount' || field === 'taxRate') {
        const amt = parseFloat(lines[idx].amount || 0);
        const rate = parseFloat(lines[idx].taxRate || 0);
        lines[idx].taxAmount = ((amt * rate) / 100).toFixed(2);
      }
      return { ...f, lines };
    });
  };

  const calcTotals = () => {
    const subtotal = form.lines.reduce((s, l) => s + parseFloat(l.amount || 0), 0);
    const taxAmt = form.lines.reduce((s, l) => s + parseFloat(l.taxAmount || 0), 0);
    return { subtotal, taxAmt, total: subtotal + taxAmt };
  };

  const handleSave = async () => {
    if (!form.cashAccountId) { setError('Please select a cash account'); return; }
    if (form.lines.some(l => !l.accountId || parseFloat(l.amount) <= 0)) { setError('Please fill all lines with account and amount'); return; }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        lines: form.lines.map(l => ({
          accountId: l.accountId,
          description: l.description,
          amount: parseFloat(l.amount),
          taxRate: parseFloat(l.taxRate || 0),
          taxAmount: parseFloat(l.taxAmount || 0),
        })),
      };
      if (selected) {
        await cpvApi.update(selected.id, payload);
      } else {
        await cpvApi.create(payload);
      }
      setDialog(false);
      loadVouchers();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handlePost = async (id) => {
    try { await cpvApi.post(id); loadVouchers(); }
    catch (err) { alert(err.response?.data?.message || 'Post failed'); }
  };

  const handleReverse = async (id) => {
    if (!window.confirm('Reverse this CPV? This creates an opposite journal entry.')) return;
    try { await cpvApi.reverse(id); loadVouchers(); }
    catch (err) { alert(err.response?.data?.message || 'Reverse failed'); }
  };

  const handleCancel = (id) => {
    setReasonDialog({ open: true, action: 'cancel', target: id });
    setReasonText('');
  };

  const handleDelete = (id) => {
    setReasonDialog({ open: true, action: 'delete', target: id });
    setReasonText('');
  };

  const handleReasonConfirm = async () => {
    const { action, target } = reasonDialog;
    setReasonDialog({ open: false, action: null, target: null });
    if (!target) return;
    try {
      if (action === 'delete') {
        await cpvApi.delete(target, reasonText || null);
      } else if (action === 'cancel') {
        await cpvApi.cancel(target, reasonText || null);
      }
      apiSuccess(action === 'delete' ? 'CPV deleted' : 'CPV cancelled');
      setReasonText('');
      loadVouchers();
    } catch (err) {
      apiError(err.response?.data?.message || 'Operation failed');
    }
  };

  const statusColor = (s) => {
    switch (s) { case 'posted': return 'success'; case 'draft': return 'default'; case 'cancelled': return 'error'; case 'reversed': return 'warning'; case 'approved': return 'info'; default: return 'default'; }
  };

  const t = calcTotals();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Cash Payment Vouchers</Typography>
          <Typography variant="body1" color="text.secondary">Record cash payments for expenses</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ fontWeight: 600 }}>
          New CPV
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" placeholder="Search voucher #, payee, description..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadVouchers(); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment> }} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select name="status" value={filters.status} onChange={handleFilterChange} label="Status">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="posted">Posted</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="reversed">Reversed</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Cash Account</InputLabel>
              <Select name="cashAccountId" value={filters.cashAccountId} onChange={handleFilterChange} label="Cash Account">
                <MenuItem value="">All</MenuItem>
                {cashAccounts.map(a => <MenuItem key={a.id} value={a.id}>{a.code} - {a.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField fullWidth size="small" label="From" type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField fullWidth size="small" label="To" type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md="auto">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" color="primary" startIcon={<Search />} onClick={loadVouchers} size="small" sx={{ whiteSpace: 'nowrap' }}>
                Search
              </Button>
              <Button variant="outlined" onClick={handleClearFilters} size="small" sx={{ whiteSpace: 'nowrap' }}>
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>CPV #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Payee</TableCell>
                <TableCell>Cash Account</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vouchers.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No CPVs found</TableCell></TableRow>
              )}
              {vouchers.map(v => (
                <TableRow key={v.id} hover>
                  <TableCell><Typography fontWeight={600}>{v.voucherNumber}</Typography></TableCell>
                  <TableCell>{v.voucherDate}</TableCell>
                  <TableCell>{v.payeeName || '-'}</TableCell>
                  <TableCell>{v.cashAccountName || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(v.totalAmount)}</TableCell>
                  <TableCell><Chip label={v.status} color={statusColor(v.status)} size="small" /></TableCell>
                  <TableCell align="center">
                    <Tooltip title="View"><IconButton size="small" onClick={() => openView(v)}><Visibility /></IconButton></Tooltip>
                    {hasPermission('cpv.edit') && ['draft', 'posted'].includes(v.status) && (
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(v)}><Edit /></IconButton></Tooltip>
                    )}
                    {v.status === 'draft' && (
                      <>
                        <Tooltip title="Post"><IconButton size="small" color="success" onClick={() => handlePost(v.id)}><PostAdd /></IconButton></Tooltip>
                        <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={() => handleCancel(v.id)}><Cancel /></IconButton></Tooltip>
                      </>
                    )}
                    {v.status === 'posted' && (
                      <Tooltip title="Reverse"><IconButton size="small" color="warning" onClick={() => handleReverse(v.id)}><Undo /></IconButton></Tooltip>
                    )}
                    {hasPermission('cpv.delete') && (
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(v.id)}><Delete /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PaginationBar
        page={page - 1}
        pageSize={limit}
        total={total}
        onPageChange={(newPage) => setPage(newPage + 1)}
        onPageSizeChange={(newSize) => { setLimit(newSize); setPage(1); }}
        loading={loading}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{selected ? 'Edit CPV' : 'New Cash Payment Voucher'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6} sm={3}>
              <TextField label="Voucher Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.voucherDate} onChange={e => setForm(f => ({ ...f, voucherDate: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SearchableSelect
                label="Cash Account"
                fullWidth
                value={form.cashAccountId}
                onChange={(v) => setForm(f => ({ ...f, cashAccountId: v }))}
                options={cashAccounts.map(a => ({ value: a.id, label: `${a.code} - ${a.name}` }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select label="Payee Type" fullWidth value={form.payeeType}
                onChange={e => setForm(f => ({ ...f, payeeType: e.target.value }))}>
                <MenuItem value="other">Other</MenuItem>
                <MenuItem value="supplier">Supplier</MenuItem>
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Payee Name" fullWidth value={form.payeeName}
                onChange={e => setForm(f => ({ ...f, payeeName: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Reference #" fullWidth value={form.referenceNumber}
                onChange={e => setForm(f => ({ ...f, referenceNumber: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Description / Narration" fullWidth multiline rows={2}
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
          </Grid>

          {/* Lines */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Account Lines</Typography>
          {form.lines.map((line, idx) => (
            <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SearchableSelect
                    label="Account"
                    size="small"
                    fullWidth
                    value={line.accountId}
                    onChange={(v) => updateLine(idx, 'accountId', v)}
                    options={accounts.map(a => ({ value: a.id, label: `${a.code} - ${a.name}` }))}
                  />
                  <QuickCreate
                    entityKey="account"
                    onCreated={(created) => {
                      if (created?.id) {
                        setAccounts((prev) =>
                          prev.some((a) => a.id === created.id)
                            ? prev
                            : [...prev, { id: created.id, code: created.code || 'ACC', name: created.name || 'Account', type: created.type || 'expense' }]
                        );
                        updateLine(idx, 'accountId', created.id);
                      }
                      loadAccounts();
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Description" fullWidth size="small" value={line.description}
                  onChange={e => updateLine(idx, 'description', e.target.value)} />
              </Grid>
              <Grid item xs={4} sm={2}>
                <TextField label="Amount" type="number" fullWidth size="small" value={line.amount}
                  onChange={e => updateLine(idx, 'amount', e.target.value)} />
              </Grid>
              <Grid item xs={3} sm={1.5}>
                <TextField label="Tax %" type="number" fullWidth size="small" value={line.taxRate}
                  onChange={e => updateLine(idx, 'taxRate', e.target.value)} />
              </Grid>
              <Grid item xs={3} sm={1.5}>
                <TextField label="Tax Amt" type="number" fullWidth size="small" value={line.taxAmount}
                  InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={2} sm={1}>
                <IconButton size="small" color="error" onClick={() => removeLine(idx)}><Delete /></IconButton>
              </Grid>
            </Grid>
          ))}
          <Button size="small" startIcon={<Add />} onClick={addLine} sx={{ mt: 1 }}>Add Line</Button>

          {/* Totals */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between"><Typography>Subtotal:</Typography><Typography fontWeight={600}>{formatCurrency(t.subtotal)}</Typography></Box>
            <Box display="flex" justifyContent="space-between"><Typography>Tax:</Typography><Typography fontWeight={600}>{formatCurrency(t.taxAmt)}</Typography></Box>
            <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop="1px solid" borderColor="divider">
              <Typography fontWeight={700}>Total:</Typography>
              <Typography fontWeight={700} color="primary" fontSize="1.1rem">{formatCurrency(t.total)}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : selected ? 'Update' : 'Save CPV'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>CPV Details - {selected?.voucherNumber}</DialogTitle>
        <DialogContent>
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}><Typography variant="caption">Voucher #</Typography><Typography fontWeight={600}>{selected.voucherNumber}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption">Date</Typography><Typography>{selected.voucherDate}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption">Status</Typography><Chip label={selected.status} color={statusColor(selected.status)} size="small" /></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption">Cash Account</Typography><Typography>{selected.cashAccount?.code} - {selected.cashAccount?.name}</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption">Payee</Typography><Typography>{selected.payeeName || '-'} ({selected.payeeType})</Typography></Grid>
              <Grid item xs={6} sm={3}><Typography variant="caption">Reference</Typography><Typography>{selected.referenceNumber || '-'}</Typography></Grid>
              <Grid item xs={12}><Typography variant="caption">Description</Typography><Typography>{selected.description || '-'}</Typography></Grid>
              {selected.journalEntry && (
                <Grid item xs={12}><Typography variant="caption">Journal Entry</Typography><Typography>{selected.journalEntry.entryNumber}</Typography></Grid>
              )}
            </Grid>
          )}
          {selected?.lines?.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" fontWeight={600}>Lines</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>#</TableCell><TableCell>Account</TableCell><TableCell>Description</TableCell><TableCell align="right">Amount</TableCell><TableCell align="right">Tax</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
                  <TableBody>
                    {selected.lines.map((l, i) => (
                      <TableRow key={i}><TableCell>{i + 1}</TableCell><TableCell>{l.accountCode} - {l.accountName}</TableCell><TableCell>{l.description}</TableCell>
                        <TableCell align="right">{formatCurrency(l.amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(l.taxAmount)}</TableCell>
                        <TableCell align="right">{formatCurrency(l.totalAmount)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          <Box mt={2} display="flex" justifyContent="space-between">
            <Typography>Total:</Typography>
            <Typography fontWeight={700}>{formatCurrency(selected?.totalAmount || 0)}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reason Dialog for Delete/Cancel */}
      <Dialog open={reasonDialog.open} onClose={() => setReasonDialog({ open: false, action: null, target: null })} fullWidth maxWidth="sm">
        <DialogTitle>{reasonDialog.action === 'delete' ? 'Delete CPV' : 'Cancel CPV'}</DialogTitle>
        <DialogContent>
          {reasonDialog.action === 'delete' && (
            <Typography sx={{ mb: 1.5 }}>
              Are you sure you want to delete this Cash Payment Voucher? This action cannot be undone.
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialog({ open: false, action: null, target: null })}>Back</Button>
          <Button variant="contained" color={reasonDialog.action === 'delete' ? 'error' : 'warning'} onClick={handleReasonConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CashPaymentVouchers;
